import { useState } from 'react';
import { MAX_INKS, mergeInks, parseColor, toHex, type Ink, type SepMode } from '@eip/engine';
import { useStore, type DocEntry } from '../state/store';
import { pushUndo, topUndoToken } from '../state/undo';
import type { PlateMeta } from '../worker/protocol';
import { Slider } from './controls';
import { addInkFromHex, coverageLabel, duplicateInk, reorderInks, withInkPatch } from '../lib/sep-ui';
import { sepRunner } from '../app/session';
import { Section } from './Section';

export function InkList({ doc, inks, mode, plates, onChange }: {
  doc: DocEntry; inks: Ink[]; mode: SepMode; plates: PlateMeta[]; onChange: (next: Ink[]) => void;
}) {
  const building = doc.sep?.building ?? false;
  const [count, setCount] = useState(6);
  const [addHex, setAddHex] = useState('#808080');
  // Transient, UI-only: which rows are checked for the next "Merge selected",
  // and which rows have their disclosure open. Neither is part of the
  // separation, so it lives here rather than going through `onChange`.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const sorted = [...inks].sort((a, b) => a.order - b.order);
  const plateFor = (id: string) => plates.find((p) => p.kind === 'ink' && p.inkId === id);

  const toggleIn = (set: Set<string>, id: string) => { const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); return next; };

  const onAdd = () => { try { onChange(addInkFromHex(inks, addHex)); } catch { /* MAX_INKS already reached; the button is disabled too */ } };
  const onDuplicate = (id: string) => { try { onChange(duplicateInk(inks, id)); } catch { /* MAX_INKS already reached; the button is disabled too */ } };
  /** Where these entries belong: this document, in screen-print mode — the only mode
   *  whose subscription turns a restored ink list into a rebuilt set of plates (and
   *  so into a films export that carries the inks the panel lists). */
  const SEP_SCOPE = { docId: doc.id, mode: 'sep' };
  /**
   * Put this ink list back. Reads the separation LIVE and replaces only its inks,
   * so an undo taken a few seconds later cannot also revert a screen or film
   * setting the shop changed in between.
   */
  const restoreInks = (previous: Ink[]) => () => {
    const st = useStore.getState();
    const separation = st.docs.find((d) => d.id === doc.id)?.sep?.separation;
    if (separation) st.setSeparation(doc.id, { ...separation, inks: previous });
  };

  /** Detect replaces a list the shop may have spent ten minutes on — the naming, the
   *  tolerances, the order — so it is undoable, like the edits below. Pushed BEFORE
   *  the request goes out: `restoreInks` captures the list as it is now, and the
   *  detect's own result lands through `setSeparation` a second or two later. */
  const onDetect = () => {
    pushUndo('Ink list', restoreInks(inks), undefined, SEP_SCOPE);
    void sepRunner.detect(doc.id, count);
  };

  // The two ink edits that LOSE something, so both are undoable and both say what
  // they did (audit #14). Everything else in this list is a value the shop can type
  // back; an ink that was deleted, or switched off and then screened, is not.
  const onDelete = (id: string) => {
    const ink = inks.find((i) => i.id === id);
    pushUndo(`Removed ${ink?.name ?? 'ink'}`, restoreInks(inks), `inks:${doc.id}:${id}`, SEP_SCOPE);
    onChange(inks.filter((i) => i.id !== id).map((i, k) => ({ ...i, order: k })));
    setSelected((prev) => { if (!prev.has(id)) return prev; const next = new Set(prev); next.delete(id); return next; });
    if (ink) useStore.getState().showToast(`Removed ${ink.name}`, { undoToken: topUndoToken() });
  };
  const onEnabled = (ink: Ink, enabled: boolean) => {
    pushUndo(enabled ? `Printing ${ink.name}` : `${ink.name} left out`, restoreInks(inks), `inks:${doc.id}:${ink.id}:enabled`, SEP_SCOPE);
    onChange(withInkPatch(inks, ink.id, { enabled }));
  };
  const onMerge = () => {
    if (selected.size < 2) return;
    const coverage: Record<string, number> = {};
    for (const id of selected) coverage[id] = plateFor(id)?.coverage ?? 0;
    // Merging loses every selected ink but one, which is exactly what the README
    // promises an Undo toast for. No key: one merge is never a continuation of the
    // one before it.
    pushUndo(`Merged ${selected.size} inks`, restoreInks(inks), undefined, SEP_SCOPE);
    onChange(mergeInks(inks, [...selected], coverage));
    setSelected(new Set());
    useStore.getState().showToast(`Merged ${selected.size} inks`, { undoToken: topUndoToken() });
  };

  return (
    <Section id="inks" title="Inks">
      <Slider label="Ink count" value={count} min={2} max={8} onChange={setCount} />
      <button type="button" onClick={onDetect} disabled={building || !doc.run}>Detect inks</button>
      {sorted.map((ink, i) => {
        const plate = plateFor(ink.id);
        const isOpen = expanded.has(ink.id);
        return (
          <div key={ink.id} className="ink-row">
            <div className="row">
              {/* Three controls that used to be a bare checkbox, the word
                  "enabled" and the word "more" (audit #11). Each says what it is
                  for, in words, beside the box or the button it belongs to. */}
              <label className="toggle" title="Tick two or more inks, then press Merge selected">
                <input type="checkbox" checked={selected.has(ink.id)} onChange={() => setSelected((prev) => toggleIn(prev, ink.id))} aria-label={`Select ${ink.name} for merge`} />
                <span className="small">Merge</span>
              </label>
              <input type="color" className="ink-swatch" value={toHex(ink.rgb)} onChange={(e) => onChange(withInkPatch(inks, ink.id, { rgb: parseColor(e.target.value) }))} aria-label={`${ink.name} colour`} />
              <input value={ink.name} onChange={(e) => onChange(withInkPatch(inks, ink.id, { name: e.target.value }))} aria-label="Ink name" style={{ width: 100 }} />
              <span className="muted small">{coverageLabel(plate)}</span>
              <button type="button" onClick={() => onChange(reorderInks(inks, ink.id, -1))} disabled={i === 0} aria-label={`Move ${ink.name} up`}>▲</button>
              <button type="button" onClick={() => onChange(reorderInks(inks, ink.id, 1))} disabled={i === sorted.length - 1} aria-label={`Move ${ink.name} down`}>▼</button>
              <label className="toggle" title="Untick to leave this ink out of the separation">
                <input type="checkbox" checked={ink.enabled} onChange={(e) => onEnabled(ink, e.target.checked)} aria-label={`Print ${ink.name}`} />
                <span className="small">Print this ink</span>
              </label>
              <button type="button" className="right" onClick={() => setExpanded((prev) => toggleIn(prev, ink.id))} aria-expanded={isOpen} aria-controls={`ink-more-${ink.id}`}>
                {isOpen ? 'Fewer settings' : 'More settings'}
              </button>
              <button type="button" onClick={() => onDelete(ink.id)} aria-label={`Delete ${ink.name}`} title={`Delete ${ink.name}`}>×</button>
            </div>
            {isOpen && (
              <div className="ink-more" id={`ink-more-${ink.id}`}>
                <Slider label="Tolerance" value={ink.tolerance} min={0.02} max={1} step={0.01} onChange={(v) => onChange(withInkPatch(inks, ink.id, { tolerance: v }))} />
                {mode === 'sim' && <Slider label="Softness" value={ink.softness} min={0.02} max={1} step={0.01} onChange={(v) => onChange(withInkPatch(inks, ink.id, { softness: v }))} />}
                <Slider label="Curve 25%" value={ink.curve[0]} min={0} max={2} step={0.05} onChange={(v) => onChange(withInkPatch(inks, ink.id, { curve: [v, ink.curve[1], ink.curve[2]] }))} />
                <Slider label="Curve 50%" value={ink.curve[1]} min={0} max={2} step={0.05} onChange={(v) => onChange(withInkPatch(inks, ink.id, { curve: [ink.curve[0], v, ink.curve[2]] }))} />
                <Slider label="Curve 75%" value={ink.curve[2]} min={0} max={2} step={0.05} onChange={(v) => onChange(withInkPatch(inks, ink.id, { curve: [ink.curve[0], ink.curve[1], v] }))} />
                <button type="button" onClick={() => onDuplicate(ink.id)} disabled={inks.length >= MAX_INKS} aria-label={`Duplicate ${ink.name}`}>Duplicate</button>
              </div>
            )}
          </div>
        );
      })}
      <div className="row">
        <input type="color" value={addHex} onChange={(e) => setAddHex(e.target.value)} aria-label="New ink colour" />
        <button type="button" onClick={onAdd} disabled={inks.length >= MAX_INKS}>Add ink</button>
        {selected.size >= 2 && <button type="button" onClick={onMerge}>Merge selected</button>}
      </div>
    </Section>
  );
}
